/**
 * <agwa-hover-card>
 *   <button slot="anchor">...</button>
 *   ...the card's content, rendered by the host only while it is open...
 * </agwa-hover-card>
 *
 * A card that opens over an anchor on hover, keyboard focus or a tap, and that is always drawn ON
 * TOP of the page, whatever it sits in. The anchor is the host's own control (the person's name in
 * <user-name>); the card's content is the host's own markup. This component decides when the card
 * is open and where it goes, and nothing about what either looks like.
 *
 * WHY THE TOP LAYER (U40 F1, the architect on the security audit log). A card positioned with a
 * z-index only wins inside its own stacking context. agwa-lit-table gives every row segment one
 * (position: relative; z-index: 0), so a card opened on a short row was painted under the rows
 * after it, and a dialog or any other stacked host would do the same. The card is a popover
 * (popover="manual"), shown with showPopover(), which puts it in the browser's top layer above
 * every stacking context on the page; its position is computed against the anchor by
 * @floating-ui/dom (below the anchor, flipping and shifting to stay in the window), and kept there
 * while it is open as the page scrolls or the anchor moves.
 *
 * HOW IT OPENS AND CLOSES (the behaviour <user-name> had, moved here so every card shares it):
 * - a mouse opens it by hovering the anchor, and it closes a moment after the pointer leaves both
 *   the anchor and the card, long enough to cross the gap between them;
 * - keyboard focus on the anchor, or on a link inside the card, opens it; focus leaving both closes
 *   it; Escape closes it and returns focus to the anchor;
 * - a tap, or Enter or Space on the focused anchor, toggles it and stays with the anchor: a touch
 *   or keyboard user has no hover, so this is their only way in, and it must not also open a
 *   clickable row around it. A mouse click is left to the row (the staff list opens the person's
 *   editor, say): the card is already open from the hover;
 * - a press anywhere outside the anchor and the card closes it.
 *
 * The host learns of every change through `hover-card-toggle` (detail: { open }), and renders the
 * card's content only while open, so text read from a row holds the anchor alone.
 */
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
} from '@floating-ui/dom';
import hoverCardStyles from './agwa-hover-card.css?inline';

/**
 * How long the card stays open after the pointer leaves the anchor or the card. Long enough to
 * cross the gap between the two without the card closing under the pointer, short enough that it
 * does not linger once the person has moved on.
 */
const CLOSE_DELAY_MS = 200;

/**
 * How long after a press its focus and click still count as that press's. A tap's click follows
 * its press within a few hundred milliseconds at most; a keyboard reach of the anchor a whole
 * second after pressing it is a separate act.
 */
const PRESS_WINDOW_MS = 1000;

/** The gap between the anchor and the card, and the margin kept from the window's edges. */
const GAP_PX = 4;
const EDGE_PX = 8;

export interface HoverCardToggleDetail {
  open: boolean;
}

@customElement('agwa-hover-card')
export class AgwaHoverCard extends LitElement {
  static styles = [unsafeCSS(hoverCardStyles)];

  /** Whether the card is showing. Changed only through setOpen, which tells the host. */
  private _open = false;
  private _closeTimer?: ReturnType<typeof setTimeout>;
  /** Stops following the anchor once the card closes; set while it is open. */
  private _stopFollowing?: () => void;
  /**
   * The last press on the anchor: its pointer type and when it happened. A tap or a mouse press
   * also focuses the anchor, and that focus must not open the card on its own: the click that
   * follows decides, and a tap that opened on focus and then toggled on click would close the card
   * it had just opened. A time rather than a flag cleared by a later event, because a touch tap
   * delivers pointerup and pointerleave BEFORE its focus and click, in tasks of their own; and it
   * is the press's pointer type the click reads, because not every engine sends the click itself
   * as a PointerEvent with a type.
   */
  private _lastPress?: { pointerType: string; at: number };
  /** Set while Escape hands focus back to the anchor, so that focus does not reopen the card. */
  private _refocusing = false;

  @query('.card') private _card?: HTMLElement;

  get open(): boolean {
    return this._open;
  }

  connectedCallback(): void {
    super.connectedCallback();
    // On the host, so the anchor and the card (both its descendants) count as one area.
    this.addEventListener('pointerenter', this.onPointerEnter);
    this.addEventListener('pointerleave', this.onPointerLeave);
    this.addEventListener('pointerdown', this.onPointerDown);
    this.addEventListener('focusin', this.onFocusIn);
    this.addEventListener('focusout', this.onFocusOut);
    this.addEventListener('keydown', this.onKeyDown);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('pointerenter', this.onPointerEnter);
    this.removeEventListener('pointerleave', this.onPointerLeave);
    this.removeEventListener('pointerdown', this.onPointerDown);
    this.removeEventListener('focusin', this.onFocusIn);
    this.removeEventListener('focusout', this.onFocusOut);
    this.removeEventListener('keydown', this.onKeyDown);
    this.removeEventListener('click', this.onClick);
    this.setOpen(false);
  }

  /**
   * Open or close the card, tell the host, and put the card in or take it out of the top layer.
   * While open, a press anywhere outside the anchor and the card closes it.
   */
  private setOpen(open: boolean) {
    clearTimeout(this._closeTimer);
    this._closeTimer = undefined;
    if (open === this._open) return;
    this._open = open;
    if (open) {
      document.addEventListener(
        'pointerdown',
        this.onDocumentPointerDown,
        true
      );
    } else {
      document.removeEventListener(
        'pointerdown',
        this.onDocumentPointerDown,
        true
      );
    }
    this.dispatchEvent(
      new CustomEvent<HoverCardToggleDetail>('hover-card-toggle', {
        detail: { open },
      })
    );
    // The host renders the content in its own update; show the card once that has landed, so it
    // is measured and placed with its content in it.
    void this.placeAfterHostRenders(open);
  }

  private async placeAfterHostRenders(open: boolean) {
    await Promise.resolve();
    const host = this.getRootNode();
    if (host instanceof ShadowRoot && host.host instanceof LitElement)
      await host.host.updateComplete;
    if (open !== this._open) return;
    const card = this._card;
    const anchor = this.anchorElement();
    if (!card || !anchor) return;
    if (open) {
      // The top layer, where supported; an engine without the Popover API still shows the card,
      // fixed-positioned, as the stylesheet's fallback does.
      if (
        typeof card.showPopover === 'function' &&
        !card.matches(':popover-open')
      )
        card.showPopover();
      card.classList.add('shown');
      this._stopFollowing?.();
      this._stopFollowing = autoUpdate(anchor, card, () =>
        this.place(anchor, card)
      );
    } else {
      this._stopFollowing?.();
      this._stopFollowing = undefined;
      card.classList.remove('shown');
      if (
        typeof card.hidePopover === 'function' &&
        card.matches(':popover-open')
      )
        card.hidePopover();
    }
  }

  /** Below the anchor, starting at its left, flipped above or shifted to stay in the window. */
  private place(anchor: Element, card: HTMLElement) {
    void computePosition(anchor, card, {
      strategy: 'fixed',
      placement: 'bottom-start',
      middleware: [
        offset(GAP_PX),
        flip({ padding: EDGE_PX }),
        shift({ padding: EDGE_PX }),
      ],
    }).then(({ x, y }) => {
      card.style.left = `${x}px`;
      card.style.top = `${y}px`;
    });
  }

  /** The host's anchor control: its child slotted as the anchor. */
  private anchorElement(): HTMLElement | undefined {
    return (
      this.querySelector<HTMLElement>(':scope > [slot="anchor"]') ?? undefined
    );
  }

  private scheduleClose() {
    clearTimeout(this._closeTimer);
    this._closeTimer = setTimeout(() => this.setOpen(false), CLOSE_DELAY_MS);
  }

  private onDocumentPointerDown = (e: PointerEvent) => {
    if (!e.composedPath().includes(this)) this.setOpen(false);
  };

  /** Hover opens it for a mouse only: a touch opens it by the tap (click) instead. */
  private onPointerEnter = (e: PointerEvent) => {
    if (e.pointerType === 'mouse') this.setOpen(true);
    else clearTimeout(this._closeTimer);
  };

  private onPointerLeave = (e: PointerEvent) => {
    if (e.pointerType === 'mouse') this.scheduleClose();
  };

  private onPointerDown = (e: PointerEvent) => {
    this._lastPress = { pointerType: e.pointerType, at: performance.now() };
  };

  /**
   * The pointer type of a press within the last second, or '' when there was none: a click with no
   * recent press came from the keyboard (Enter or Space on the focused anchor). A press dragged off
   * the anchor and released elsewhere simply ages out, so later keyboard focus still opens the card.
   */
  private pressedJustNow(): string {
    const press = this._lastPress;
    return press && performance.now() - press.at < PRESS_WINDOW_MS
      ? press.pointerType
      : '';
  }

  /** Reaching the anchor, or a link in the card, with the keyboard keeps the card open. */
  private onFocusIn = () => {
    if (this.pressedJustNow() || this._refocusing) return;
    this.setOpen(true);
  };

  /** Focus leaving both the anchor and the card closes it; moving between them does not. */
  private onFocusOut = (e: FocusEvent) => {
    const next = e.relatedTarget as Node | null;
    if (next && (this.contains(next) || this.shadowRoot?.contains(next)))
      return;
    this.scheduleClose();
  };

  /**
   * See the file comment: a tap or a key press toggles and stays; a mouse click reaches the row. A
   * click inside the card (a link) is the card's own and is left alone. Read on the host, by
   * whether the click came from the anchor child, so it does not rest on slot event paths.
   */
  private onClick = (e: MouseEvent) => {
    const anchor = this.anchorElement();
    if (!anchor || !e.composedPath().includes(anchor)) return;
    const pointerType = this.pressedJustNow();
    this._lastPress = undefined;
    if (pointerType === 'mouse') {
      this.setOpen(true);
      return;
    }
    e.stopPropagation();
    this.setOpen(!this._open);
  };

  /**
   * Escape closes the card and returns focus to the anchor, so the keyboard is never stranded.
   *
   * Enter or Space on the anchor goes no further than the card (#398 C14 i). Its default action is
   * the click that toggles the card (onClick), and a clickable row around the anchor opens on those
   * keys and cancels them, so the row opened instead of the card. Only propagation stops; the
   * default is left to make the click.
   */
  private onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      const anchor = this.anchorElement();
      if (anchor && e.composedPath().includes(anchor)) e.stopPropagation();
      return;
    }
    if (e.key !== 'Escape' || !this._open) return;
    e.stopPropagation();
    this.setOpen(false);
    this._refocusing = true;
    this.anchorElement()?.focus();
    this._refocusing = false;
  };

  // No whitespace between the tags: the anchor sits inline in sentences such as "by <name>.",
  // where any text node here would render as a stray space. The card is a span for the same
  // reason: a block element here lets the formatter put whitespace before it.
  render() {
    return html`<span class="anchor"><slot name="anchor"></slot></span
      ><span class="card" popover="manual"><slot></slot></span>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'agwa-hover-card': AgwaHoverCard;
  }
  interface HTMLElementEventMap {
    'hover-card-toggle': CustomEvent<HoverCardToggleDetail>;
  }
}
